import { Fragment } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import ContentWrapper from '~/tutor-on-demand/components/Content/ContentWrapper'
import HeaderTitle from '~/tutor-on-demand/components/Header/HeaderTitle'
import HeaderWrapper from '~/tutor-on-demand/components/Header/HeaderWrapper'
import List from '~/tutor-on-demand/components/Typography/List'
import TutorOnDemandProductCards from '~/shared/components/TutorOnDemandProductCards/TutorOnDemandProductCards.component'
import Paragraph from '~/tutor-on-demand/components/Typography/Paragraph'
import SubContainer from '~/tutor-on-demand/components/Content/SubContainer'
import SubContainerWrapper from '~/tutor-on-demand/components/Content/SubContainerWrapper'
import { SubjectType, getSubjectImage } from '~/components/Metadata/SubjectMetatags'
import { Breakpoint, useCheckBreakpoint } from '~/utils'

import ROUTES from '~/shared/constants/routes.const'
import BreadCrumbs from '~/encyclopedia/components/BreadCrumbs'

enum Topic {
  VELOCITY_AND_ACCELARATION = 'velocity-and-accelaration',
  GRAVITY = 'gravity',
  ENERGY = 'energy',
  FORCES = 'forces',
  WORK = 'work',
  POWER = 'power',
  MOMENTUM = 'momentum',
  NEWTONS_LAWS_OF_MOTION = 'newtons-laws-of-motion',
  CURVILINEAR_MOTION = 'curvilinear-motion',
  ELECTRICITY = 'electricity',
  MAGNETISM = 'magnetism',
  RADIATION = 'radiation',
  INDUCTION = 'induction',
  FUSION = 'fusion',
  QUANTUM_PHYSICS = 'quantum-physics',
}

const physicsLevels = ['Fysikk 1/REA 3004', 'Fysikk 2/REA 3005 og REA 3006']

export default function TutorOnDemandPhysics({ type }: { type: SubjectType }): React.ReactElement {
  const { t } = useTranslation(['tutorOnDemand', 'tutorOnDemandPhysics'])
  const breadcrumbs = [
    { path: ROUTES.TUTOR_ON_DEMAND_INFORMATION, h1: t('tutorOnDemand:landingPage.whatIsTutorOnDemand.title') },
    { path: ROUTES.TUTOR_ON_DEMAND_PHYSICS, h1: t('tutorOnDemandPhysics:title') },
  ]
  const { locale } = useRouter()
  const isSmallScreen = useCheckBreakpoint({ lt: Breakpoint.XL })

  return (
    <Fragment>
      <HeaderWrapper>
        <BreadCrumbs
          breadcrumbs={breadcrumbs}
          fontSize="medium"
          isWhite
          itemClassNames="font-medium items-center"
          wrapperClassNames="mt-1 pr-12 lg:pr-0"
        />

        <HeaderTitle
          iconAlt={getSubjectImage(type, t).alt as string}
          iconSrc={getSubjectImage(type, t).srcKey as string}
        >
          {t('tutorOnDemandPhysics:title')}
        </HeaderTitle>
      </HeaderWrapper>
      <ContentWrapper className="space-y-8">
        <div className="xl:space-x-8 xl:flex">
          <section className="mb-6 space-y-6 xl:mb-0">
            <Paragraph>{t('tutorOnDemandPhysics:contentParagraph1')}</Paragraph>
            <Paragraph>{t('tutorOnDemandPhysics:contentParagraph2')}</Paragraph>
            <Paragraph>{t('tutorOnDemandPhysics:contentParagraph3')}</Paragraph>
            {!isSmallScreen && (
              <TutorOnDemandProductCards
                className="pt-8 max-w-max space-y-14 lg:space-y-0 lg:space-x-4 xl:space-x-8 flex flex-col lg:flex-row"
                colorScheme="white"
                hideChatWithTutor
                hideVideoChatWithTutor
              />
            )}
          </section>
          <SubContainerWrapper>
            <SubContainer className="w-full max-w-sm" header={t('tutorOnDemandPhysics:weCanHelpYouWith.header')}>
              <List
                items={Object.values(Topic).map((item) => t(`tutorOnDemandPhysics:weCanHelpYouWith.items.${item}`))}
              />
            </SubContainer>
            {locale === 'no' && (
              <SubContainer className="w-full max-w-sm" header="Vi kan alle fag i fysikk, som for eksempel">
                <List items={physicsLevels} />
              </SubContainer>
            )}
          </SubContainerWrapper>
        </div>
        {isSmallScreen && (
          <TutorOnDemandProductCards
            className="pt-8 max-w-max space-y-14 lg:space-y-0 lg:space-x-4 xl:space-x-8 flex flex-col lg:flex-row"
            colorScheme="white"
            hideChatWithTutor
            hideVideoChatWithTutor
          />
        )}
      </ContentWrapper>
    </Fragment>
  )
}
